import { HomePageTile } from "slot-definitions"
import { Alert, AlertTitle, Box, Button, Chip, Stack, Typography } from "@mui/material"
import { useDemand } from "lib/authorization/use-demand"
import { Case, Switch } from "lib/@components/switch"
import useLocalStorage from "minimals-template/components/@hooks/useLocalStorage"
import Iconify from "minimals-template/components/Iconify"
import { ListItemBox } from "lib/@components/ListItemBox"

const BANNER_EXPIRY_DATE = new Date("2026-02-07")
const STORAGE_KEY = "fiq-features-banner-dismissed"
const FEATURES_URL = "https://www.sfg20.co.uk/product-updates"

HomePageTile.plug(<Banner priority={85} />)

function Banner() {
    const [dismissed, setDismissed] = useLocalStorage(STORAGE_KEY, false)
    const hasPro = useDemand("feature-asset-mapping-schedule-library-updates")
    const hasAtLeastAdvanced = useDemand("feature-asset-mapping-tailored")
    const hasAdvanced = hasAtLeastAdvanced && !hasPro
    const level = !hasPro && !hasAdvanced ? "foundation" : hasPro ? "pro" : "advanced"

    // Don't show if banner has expired
    if (new Date() > BANNER_EXPIRY_DATE) {
        return null
    }

    // Don't show if user has dismissed the banner
    if (dismissed) {
        return null
    }

    return (
        <Box mt={2} ml={2} width={1}>
            <Alert
                severity="info"
                icon={
                    <Box
                        sx={{
                            bgcolor: "sfg20.mainBg",
                            borderColor: "sfg20.mainBorder",
                            borderWidth: 1,
                            borderStyle: "solid",
                        }}
                        p={1}
                        borderRadius={1}
                        height={42}
                    >
                        <Iconify sx={{ color: "sfg20.main" }} icon="lucide:stars" width={24} height={24} />
                    </Box>
                }
                sx={{
                    backgroundColor: "sfg20.dark",
                    color: "sfg20.light",
                    borderRadius: 2,
                    p: 2,
                    pt: 1,
                    pb: 1,
                    "& .MuiAlert-icon": {
                        color: "sfg20.main",
                    },
                    "& .MuiPaper-root": {
                        width: 1,
                    },
                }}
                action={
                    <Stack direction="row" spacing={1} sx={{ my: "auto", mr: 1 }} alignItems="center">
                        <Button
                            variant="contained"
                            color="primary"
                            href={FEATURES_URL}
                            target="_blank"
                            rel="noopener"
                            sx={{ whiteSpace: "nowrap" }}
                            endIcon={<Iconify icon="material-symbols:arrow-right-alt-rounded" />}
                        >
                            {level === "pro" ? "Explore What's New" : "Learn more"}
                        </Button>

                        <Button
                            sx={{ paddingLeft: 0, paddingRight: 0, minWidth: 28 }}
                            variant="contained"
                            aria-label="close"
                            color="primary"
                            size="small"
                            onClick={handleDismiss}
                        >
                            <Iconify icon="mdi:close" width={20} height={20} />
                        </Button>
                    </Stack>
                }
            >
                <Switch value={level}>
                    <Case when="foundation">
                        <AlertTitle>
                            Unlock the full power of Facilities-iQ!{" "}
                            <Chip
                                sx={{ bgcolor: "primary.main", position: "relative", top: -2, left: 4, color: "white" }}
                                size="small"
                                label={
                                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                                        NEW
                                    </Typography>
                                }
                            />
                        </AlertTitle>
                        <Box maxWidth="40em">
                            Upgrade to bulk-apply task frequencies to 0Us, review your regimes against the latest
                            version of SFG20 in one place, and see the impact of updates on task time and effort.
                        </Box>
                    </Case>
                    <Case when="advanced">
                        <AlertTitle>
                            Powerful new time saving Facilities-iQ feature now live!{" "}
                            <Chip
                                sx={{ bgcolor: "primary.main", position: "relative", top: -2, left: 4, color: "white" }}
                                size="small"
                                label={
                                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                                        NEW
                                    </Typography>
                                }
                            />
                        </AlertTitle>
                        <Box maxWidth="40em">
                            Now you can review all of your tailoring in one place! Professional users get even more
                            features like bulk updates to 0Us and comparisons between new and previous regimes to see
                            the impact of updates on task time and effort.
                        </Box>
                    </Case>
                    <Case when="pro">
                        <AlertTitle>
                            Powerful new time saving Facilities-iQ features now live!{" "}
                            <Chip
                                sx={{ bgcolor: "primary.main", position: "relative", top: -2, left: 4, color: "white" }}
                                size="small"
                                label={
                                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                                        NEW
                                    </Typography>
                                }
                            />
                        </AlertTitle>
                        <Box maxWidth="40em">
                            Now you can bulk-apply task frequencies to 0Us, compare new and previous regimes to see the
                            impact of updates on task time and effort, and track SFG20 library updates in one place so
                            critical changes are never missed.
                        </Box>
                    </Case>
                </Switch>
                <ListItemBox>
                    <Box flex={1} />
                </ListItemBox>
            </Alert>
        </Box>
    )

    function handleDismiss() {
        setDismissed(true)
    }
}
